import { useApolloClient } from "@apollo/client";
import { getCookie, setCookie } from "cookies-next";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { useStore } from "@hooks/useZustandProvider";
import {
  readPreferredLanguage,
  updatePreferredLanguage,
} from "@lib/preferredLanguage";
import {
  getSupportedPreferredLanguage,
  PREFERRED_LOCALE_COOKIE,
} from "@utils/preferredLanguage";

const emptyState = { value: null, loading: false, saving: false, error: null };
const PreferredLanguageContext = createContext({
  ...emptyState,
  available: false,
  refresh: async () => {},
  save: async () => "cancelled",
});

export function PreferredLanguageProvider({ children }) {
  const client = useApolloClient();
  const { user } = useStore();
  const token = getCookie("token");
  const identity = user.id ?? user.email;
  const available = Boolean(
    token && identity && !user.isCorporate && !getCookie("corporate-token"),
  );
  const sessionKey = available ? `${identity}:${token}` : null;
  const activeSession = useRef(sessionKey);
  activeSession.current = sessionKey;
  const [state, setState] = useState(emptyState);
  const mounted = useRef(false);
  const revision = useRef(0);
  const busy = useRef(false);

  const isCurrentSession = useCallback(
    () =>
      mounted.current &&
      sessionKey !== null &&
      activeSession.current === sessionKey &&
      getCookie("token") === token &&
      !getCookie("corporate-token") &&
      !getCookie("extraLogout"),
    [token, sessionKey],
  );

  const confirm = useCallback((value) => {
    if (value) {
      setCookie(PREFERRED_LOCALE_COOKIE, value, {
        path: "/",
        sameSite: "lax",
        secure: window.location.protocol === "https:",
        maxAge: 60 * 60 * 24 * 365,
      });
    }
  }, []);

  const refresh = useCallback(async () => {
    if (busy.current || !isCurrentSession()) return;
    const requestRevision = ++revision.current;
    setState((current) => ({
      ...(current.sessionKey === sessionKey ? current : emptyState),
      sessionKey,
      loading: true,
      error: null,
    }));
    try {
      const value = await readPreferredLanguage(client);
      if (!isCurrentSession() || requestRevision !== revision.current) return;
      confirm(value);
      setState({ ...emptyState, sessionKey, value });
    } catch {
      if (!isCurrentSession() || requestRevision !== revision.current) return;
      setState((current) => ({ ...current, loading: false, error: "read" }));
    }
  }, [client, confirm, isCurrentSession, sessionKey]);

  useEffect(() => {
    mounted.current = true;
    busy.current = false;
    if (sessionKey) refresh();
    else setState({ ...emptyState, sessionKey: null });
    return () => {
      mounted.current = false;
      revision.current++;
    };
  }, [refresh, sessionKey]);

  const save = useCallback(
    async (value) => {
      if (
        busy.current ||
        state.loading ||
        state.sessionKey !== sessionKey ||
        !isCurrentSession() ||
        !getSupportedPreferredLanguage(value)
      ) {
        return "cancelled";
      }
      busy.current = true;
      const requestRevision = ++revision.current;
      const isCurrent = () =>
        isCurrentSession() && requestRevision === revision.current;
      setState((current) => ({
        ...current,
        loading: false,
        saving: true,
        error: null,
      }));
      try {
        await updatePreferredLanguage(client, value);
        if (!isCurrent()) return "cancelled";
        const confirmed = await readPreferredLanguage(client);
        if (!isCurrent()) return "cancelled";
        if (confirmed !== value) throw new Error("Language was not confirmed");
        confirm(confirmed);
        setState({ ...emptyState, sessionKey, value: confirmed });
        return "saved";
      } catch {
        if (!isCurrent()) return "cancelled";
        setState((current) => ({ ...current, saving: false, error: "save" }));
        return "failed";
      } finally {
        if (isCurrent()) busy.current = false;
      }
    },
    [
      client,
      confirm,
      isCurrentSession,
      sessionKey,
      state.loading,
      state.sessionKey,
    ],
  );

  // Keep the provider and its children mounted across login/logout. Only the
  // preference state changes, so checkout forms retain their local state.
  const visibleState =
    state.sessionKey === sessionKey
      ? state
      : { ...emptyState, loading: available };
  return (
    <PreferredLanguageContext.Provider
      value={{ ...visibleState, available, refresh, save }}
    >
      {children}
    </PreferredLanguageContext.Provider>
  );
}

export const usePreferredLanguage = () => useContext(PreferredLanguageContext);
